/**
 * ProductItemAddModal 컴포넌트
 *
 * 상품 아이템 추가 모달
 * - 강의 선택
 * - 가격 정보 입력 (원가, 할인가)
 */

import { CourseFilterKey } from '@repo/types';
import { PRODUCT_ITEM_KIND, PRODUCT_KIND, PRODUCT_PACKAGE_KIND } from '@theailab-dev/parchment';
import type { ProductItemKind, ProductKind, ProductPackageKind } from '@theailab-dev/parchment';
import React, { useState } from 'react';
import { HiX } from 'react-icons/hi';
import { Badge } from './Badge';
import { Button } from './Button';
import ConfirmModal from './ConfirmModal';
import { Input } from './Input';
import { LoadingSpinner } from './LoadingSpinner';
import { ProductItemDto } from '../../api/productApi';
import { useCourses } from '../../hooks/useCourses';
import { useProductItemForm } from '../../hooks/useProductItemForm';

// 할인가 입력 컴포넌트 (천단위 콤마 지원)
const DiscountInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}> = ({ value, onChange, disabled, placeholder }) => {
  const [isFocused, setIsFocused] = useState(false);

  // 숫자만 추출하여 천단위 콤마 적용
  const formatValue = (val: string): string => {
    if (!val || val === '0') return '';
    const numericValue = val.replace(/[^0-9]/g, '');
    if (!numericValue) return '';
    return parseInt(numericValue, 10).toLocaleString();
  };

  // 표시할 값 결정
  const displayValue = isFocused ? (value === '0' ? '' : value.replace(/,/g, '')) : formatValue(value);

  return (
    <input
      id="discount"
      type="text"
      value={displayValue}
      onChange={(e) => {
        // 숫자만 추출
        const numericValue = e.target.value.replace(/[^0-9]/g, '');
        // 앞의 0 제거 (단, 빈 값은 허용)
        const cleanedValue = numericValue.replace(/^0+/, '') || '0';
        onChange(cleanedValue);
      }}
      onFocus={() => {
        setIsFocused(true);
        // 포커스 시 0이면 빈 문자열로 변경
        if (value === '0') {
          onChange('');
        }
      }}
      onBlur={() => {
        setIsFocused(false);
        // 포커스 해제 시 빈 값이면 0으로 설정 (하지만 표시는 안 함)
        if (!value || value === '') {
          onChange('0');
        }
      }}
      disabled={disabled}
      placeholder={placeholder || '0'}
      className="block w-full rounded-lg border border-slate-200 px-3 py-2.5 text-right shadow-sm transition-colors duration-200 focus:border-blue-500 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500 sm:text-sm"
    />
  );
};

interface ProductItemAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    courseId: number | null;
    kind: ProductItemKind;
    originalPrice: number;
    discount: number;
    packageKind?: ProductPackageKind;
  }) => Promise<void>;
  productId: number;
  productKind: ProductKind; // 상품 유형
  existingItemCount: number; // 현재 등록된 아이템 개수
  existingCourseIds?: number[]; // 이미 추가된 강의 ID 목록 (중복 방지용)
  existingItems?: ProductItemDto[]; // 현재 상품에 등록된 아이템 목록 (원가 조회용)
}

const ProductItemAddModal: React.FC<ProductItemAddModalProps> = ({
  isOpen,
  onClose,
  onSave,
  productId: _productId,
  productKind,
  existingItemCount,
  existingCourseIds = [],
  existingItems = [],
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const limit = 5;

  // 강의 검색 훅
  const { data, isLoading } = useCourses({
    page,
    limit,
    filterKey: searchQuery ? CourseFilterKey.SEARCH : undefined,
    filterValue: searchQuery || undefined,
  });

  // 폼 로직 훅 사용
  // - 공통 폼 상태 및 핸들러를 가져옵니다.
  // - onSubmit: 실제 저장 로직 (API 호출 등)
  // - customValidator: 추가 모달 전용 유효성 검사 (단일 상품 개수 제한 등)
  const {
    discount,
    setDiscount,
    selectedCourse,
    packageKind,
    setPackageKind,
    isSubmitting,
    alertModal,
    showAlert,
    closeAlert,
    handleCourseSelect,
    validateAndSubmit,
    originalPrice,
  } = useProductItemForm({
    productKind,
    existingItems,
    isOpen,
    onClose,
    onSubmit: async (data) => {
      await onSave({
        courseId: selectedCourse?.id ?? null, // 추가 모달에서는 courseId 필요
        kind: PRODUCT_ITEM_KIND.COURSE, // 현재는 강의만 지원
        originalPrice: data.originalPrice,
        discount: data.discount,
        packageKind: data.packageKind,
      });
      // 성공 시 폼 초기화
      setSearchQuery('');
      setPage(1);
    },
    customValidator: async () => {
      // 1. 단일 상품 개수 제한 검사
      // - 단일 상품(SINGLE)은 아이템을 1개만 가질 수 있음
      if (productKind === PRODUCT_KIND.SINGLE && existingItemCount >= 1) {
        showAlert(
          'warning',
          '추가 불가',
          '단일 상품은 강의를 1개만 등록할 수 있습니다.\n기존 강의를 삭제한 후 추가해주세요.',
        );
        return false;
      }

      // 2. 원가 정보 확인
      // - 강의 선택 시 자동으로 가져온 original_price 사용
      // - 원가가 없으면 추가 불가
      if (originalPrice === null) {
        showAlert(
          'error',
          '입력 오류',
          '강의의 원가 정보를 불러올 수 없습니다.\n강의가 product_item과 연결되어 있는지 확인해주세요.',
        );
        return false;
      }

      if (Number(originalPrice) < 0) {
        showAlert('error', '입력 오류', '원가는 0원 이상이어야 합니다.');
        return false;
      }

      return true;
    },
  });

  const handleClose = () => {
    if (!isSubmitting) {
      setSearchQuery('');
      setPage(1);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-2xl">
        {/* 헤더 */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white p-4">
          <h2 className="text-xl font-semibold text-slate-800">상품 아이템 추가</h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="rounded-lg p-1 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50">
            <HiX className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {/* 내용 */}
        <div className="p-6">
          <div className="space-y-4">
            {/* 강의 선택 섹션 */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">강의 선택</label>
              <div className="flex gap-2">
                <Input
                  id="course-search"
                  placeholder="강의 제목 검색..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  className="flex-1"
                  disabled={isSubmitting}
                />
              </div>

              {/* 강의 목록 */}
              <div className="max-h-[300px] min-h-[200px] overflow-y-auto rounded-lg border border-slate-200">
                {isLoading ? (
                  <div className="flex h-full items-center justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : data?.data.length === 0 ? (
                  <div className="flex h-full items-center justify-center py-8 text-slate-500">
                    검색 결과가 없습니다.
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {data?.data.map((course) => {
                      const isSelected = selectedCourse?.id === course.id;
                      const isExisting = existingCourseIds.includes(course.id);
                      return (
                        <li
                          key={course.id}
                          className={`flex items-center justify-between p-3 transition-colors ${
                            isExisting
                              ? 'cursor-not-allowed bg-slate-50 opacity-50'
                              : isSelected
                                ? 'cursor-pointer bg-blue-50'
                                : 'cursor-pointer hover:bg-slate-50'
                          }`}
                          onClick={() => !isExisting && handleCourseSelect(course)}>
                          <div className="flex items-center gap-3 overflow-hidden">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-slate-900">{course.title}</p>
                              <div className="mt-0.5 flex items-center gap-2">
                                <Badge variant="neutral" size="sm" className="px-1.5 py-0 text-[10px]">
                                  {course.kind}
                                </Badge>
                                <span className="truncate text-xs text-slate-500">
                                  {course.instructor?.name || '강사 미정'}
                                </span>
                              </div>
                            </div>
                          </div>
                          {isSelected && (
                            <span className="flex-shrink-0 text-xs font-medium text-blue-600">선택됨</span>
                          )}
                          {isExisting && (
                            <span className="flex-shrink-0 text-xs font-medium text-slate-400">이미 추가됨</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* 페이지네이션 */}
              {data && data.meta.totalPages > 1 && (
                <div className="flex items-center justify-between pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1 || isSubmitting}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}>
                    이전
                  </Button>
                  <span className="text-sm text-slate-500">
                    {page} / {data.meta.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= data.meta.totalPages || isSubmitting}
                    onClick={() => setPage((p) => p + 1)}>
                    다음
                  </Button>
                </div>
              )}
            </div>

            {/* 가격 정보 섹션 */}
            {selectedCourse && (
              <div className="space-y-4 border-t border-slate-200 pt-4">
                {/* 원가 표시 (강의 선택 시 자동으로 가져옴) */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">원가 (원)</label>
                  {originalPrice !== null ? (
                    <div className="flex min-h-[42px] items-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                      <span>{originalPrice.toLocaleString()}원</span>
                    </div>
                  ) : (
                    <div className="flex min-h-[42px] items-center rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                      <span>해당 강의의 원가 정보를 찾을 수 없습니다.</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">
                    할인가 (원) <span className="font-normal text-slate-400">(선택사항)</span>
                  </label>
                  <DiscountInput
                    value={discount}
                    onChange={setDiscount}
                    disabled={isSubmitting}
                    placeholder="예: 10,000"
                  />
                  {originalPrice !== null && discount && discount !== '0' && (
                    <p className="text-xs text-slate-500">
                      최종 가격:{' '}
                      <span className="font-semibold text-slate-900">
                        {(originalPrice - (parseFloat(discount.replace(/,/g, '')) || 0)).toLocaleString()}원
                      </span>
                    </p>
                  )}
                </div>

                {/* 패키지 유형 선택 */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">
                    패키지 유형 <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    value={packageKind}
                    onChange={(e) => {
                      const newKind = e.target.value as ProductPackageKind;
                      // REQUIREMENT는 최소 1개 필수 검증
                      // packageKind가 없으면 기본값으로 REQUIREMENT로 간주
                      const existingRequirementCount = existingItems.filter(
                        (item) => item.packageKind === PRODUCT_PACKAGE_KIND.REQUIREMENT || !item.packageKind,
                      ).length;

                      if (newKind === PRODUCT_PACKAGE_KIND.OPTION && existingRequirementCount === 0) {
                        showAlert(
                          'error',
                          '선택 불가',
                          '필수(REQUIREMENT) 아이템이 최소 1개 이상 있어야 합니다.\n현재 필수 아이템이 없으므로 이 아이템은 필수로 설정해야 합니다.',
                        );
                        return;
                      }
                      setPackageKind(newKind);
                    }}
                    disabled={isSubmitting}>
                    <option value={PRODUCT_PACKAGE_KIND.REQUIREMENT}>필수 (REQUIREMENT)</option>
                    <option value={PRODUCT_PACKAGE_KIND.OPTION}>선택 (OPTION)</option>
                  </select>
                  <p className="text-xs text-slate-500">
                    {existingItems.filter(
                      (item) => item.packageKind === PRODUCT_PACKAGE_KIND.REQUIREMENT || !item.packageKind,
                    ).length === 0
                      ? '⚠️ 필수 아이템이 없습니다. 이 아이템은 필수로 설정해야 합니다.'
                      : `현재 필수 아이템: ${existingItems.filter((item) => item.packageKind === PRODUCT_PACKAGE_KIND.REQUIREMENT || !item.packageKind).length}개`}
                  </p>
                </div>
              </div>
            )}

            {/* 버튼 */}
            <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                취소
              </Button>
              <Button
                variant="primary"
                onClick={validateAndSubmit}
                disabled={!selectedCourse || originalPrice === null || isSubmitting}>
                {isSubmitting ? '추가 중...' : '추가'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* 알림 모달 */}
      <ConfirmModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </div>
  );
};

export default ProductItemAddModal;
